import * as React from 'react'
import ImageView from '../ui/ImageView'
import { IProviderProductFilters, IProviderProductObject } from '../../types'
import ReactTablePagination from '../ui/ReactTablePagination'
import PencilGreyIcon from '../icons/ui/PencilGreyIcon'
import ContextMenu from '../ui/ContextMenu'
import CrossCircledIcon from '../icons/ui/CrossCircledIcon'

import { useTable, usePagination, Column, useSortBy, useRowState } from 'react-table'
import { Link, RouteComponentProps, withRouter } from 'react-router-dom'
import { MoonLoader } from 'react-spinners'
import { useEffect, useRef } from 'react'
import classNames from 'classnames'

export interface IProviderProductListProps extends RouteComponentProps<any> {
  organizationId: string
  products?: IProviderProductObject[]
  productId?: IProviderProductObject
  productCount: number
  query?: string
  type: string
  filters: IProviderProductFilters
  isDeactive: boolean
  isActive: boolean
  onComponentMount: (params: IProviderProductFilters) => void
  showDeleteDialog: (product: IProviderProductObject) => () => void
  showActivateDialog: (product: IProviderProductObject) => () => void
  showDeactivateDialog: (product: IProviderProductObject) => () => void
  updateProductFilters: (params: IProviderProductFilters) => Promise<any>
}

interface IProps<T extends object> {
  columns: Array<Column<T>>
  data: T[]
  loading: boolean
  pageCount: number
  fetchData: (params: { pageSize: number; pageIndex: number; active: boolean }) => void
  updateFilters: (params: IProviderProductFilters) => Promise<any>
  filters: IProviderProductFilters
  type: string
  query: string | undefined
}

const PAGE_SIZE = 10 //max

const SORT_ORDER = {
  ASC: 'ASC',
  DESC: 'DESC'
} as const

const SORT_FIELDS = {
  PRODUCT_NAME: 'providers_products.name'
} as const

function Table<T extends object>({
  columns,
  data,
  fetchData,
  updateFilters,
  loading,
  pageCount: controlledPageCount,
  type,
  filters,
  query
}: IProps<T>) {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    canPreviousPage,
    canNextPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, sortBy, pageSize }
  } = useTable<T>(
    {
      columns,
      data,
      initialState: {
        pageIndex: 0,
        pageSize: PAGE_SIZE
      },
      manualSortBy: true,
      manualPagination: true,
      pageCount: controlledPageCount
    },
    useSortBy,
    usePagination,
    useRowState
  )

  const mounted = useRef<boolean>()
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true
    } else {
      fetchData({ pageSize, pageIndex, active: type === 'active' })
    }
  }, [pageIndex, pageSize])

  useEffect(() => {
    let sort = ''
    let orderBy = ''
    sortBy.map(s => {
      s.desc ? (sort = 'DESC') : (sort = 'ASC')
      orderBy = s.id
    })
    if (sort && orderBy) {
      updateFilters({ orderBy, sort, active: type === 'active' })
    }
  }, [sortBy])

  return (
    <React.Fragment>
      {loading ? (
        <div className='flex justify-end' style={{ position: 'absolute', width: '90%', height: '54px' }}>
          <MoonLoader sizeUnit={'px'} size={40} color={'#405264'} loading={true} />
        </div>
      ) : null}
      <table {...getTableProps()}>
        <thead>
          {headerGroups.map(headerGroup => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map(column => (
                <th
                  className={classNames('', {
                    sortable: column.canSort,
                    sortedAsc: column.isSortedDesc === false,
                    sortedDesc: column.isSortedDesc === true
                  })}
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                  key={column.id}
                >
                  {column.render('Header')}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map(
            row =>
              +prepareRow(row) || (
                <tr {...row.getRowProps()} className='height-112' key={row.id}>
                  {row.cells.map(cell => {
                    return (
                      <td {...cell.getCellProps()} key={`productTableColumn-${cell.column.id}`}>
                        {cell.render('Cell')}
                      </td>
                    )
                  })}
                </tr>
              )
          )}
        </tbody>
      </table>
      <ReactTablePagination
        page={pageIndex}
        pages={controlledPageCount}
        pageSize={pageSize}
        canNext={query ? false : canNextPage}
        canPrevious={query ? false : canPreviousPage}
        nextPage={nextPage}
        previousPage={previousPage}
        setPageSize={setPageSize}
        rowCount={rows.length}
      />
    </React.Fragment>
  )
}

function ProviderProductList(props: IProviderProductListProps) {
  const [data, setData] = React.useState<IProviderProductObject[]>([])
  const [loading, setLoading] = React.useState(false)
  const [pageCount, setPageCount] = React.useState(3)
  const [pageSize, setPageSize] = React.useState(PAGE_SIZE)

  const columns = React.useMemo(
    () => [
      {
        Header: 'Icon',
        id: 'providers_products.id',
        className: 'flex items-center',
        accessor: (product: IProviderProductObject) => {
          return (
            <Link to={`/products/${product.id}/info`}>
              <ImageView name={product.name} entityId={product.id} />
            </Link>
          )
        }
      },
      {
        Header: 'Product Name',
        id: 'providers_products.name',
        className: 'flex items-center',
        accessor: (product: IProviderProductObject) => {
          return <Link to={`/products/${product.id}/info`}> {product.name}</Link>
        }
      },
      {
        Header: 'Service Level',
        id: 'providerServiceLevel.name',
        className: 'flex items-center',
        accessor: (product: IProviderProductObject) => {
          return (
            <Link to={`/products/${product.id}/info`}>
              {product.providerServiceLevel ? product.providerServiceLevel.name : null}
            </Link>
          )
        }
      },
      {
        Header: 'Integration Status',
        id: 'integrationStatus',
        className: 'flex items-center',
        disableSortBy: true
      },
      {
        Header: 'Description',
        id: 'description',
        className: 'flex items-center',
        disableSortBy: true,
        style: { overflow: 'no-wrap' },
        accessor: 'description'
      },
      {
        Header: 'Allotted Loading Time',
        id: 'estimatedLoadingTime',
        className: 'flex items-center',
        disableSortBy: true,
        accessor: (product: IProviderProductObject) => {
          return <div>{product.estimatedLoadingTime ? product.estimatedLoadingTime + ' minutes' : 'Not Set'}</div>
        }
      },
      {
        Header: '',
        id: 'context-menu',
        className: '',
        disableSortBy: true,
        accessor: (product: IProviderProductObject) => {
          const { products, type } = props
          if (products && type) {
            return (
              <CellContextMenu
                products={product}
                type={type}
                showDeleteDialog={props.showDeleteDialog}
                showActivateDialog={props.showActivateDialog}
                showDeactivateDialog={props.showDeactivateDialog}
                filters={props.filters}
              />
            )
          } else {
            return <span>No Menu</span>
          }
        }
      }
    ],
    []
  )
  const CellContextMenu = (props: {
    products: any
    type: string
    filters: IProviderProductFilters
    showDeleteDialog: (product: IProviderProductObject) => () => void
    showActivateDialog: (product: IProviderProductObject) => () => void
    showDeactivateDialog: (product: IProviderProductObject) => () => void
  }) => {
    const { products, type, showDeleteDialog, showActivateDialog, showDeactivateDialog } = props
    const handleRemoveClick = () => {
      return showDeleteDialog(products)
    }

    const handleActivateClick = () => {
      return showActivateDialog(products)
    }

    const handleDeactivateClick = () => {
      return showDeactivateDialog(products)
    }

    return (
      <div className='flex items-center'>
        <ContextMenu>
          <div className='MenuItem cursor--pointer'>
            <Link to={`/products/${products.id}/edit`}>
              <div className='flex items-center py1 px1'>
                <PencilGreyIcon className='icon--small svg-stroke-grey3 mr2' />
                <span className='font-dark-grey h5'>Edit</span>
              </div>
            </Link>
          </div>
          {type === 'deactivated' ? (
            <React.Fragment>
              <div className='MenuItem cursor--pointer'>
                <div className='flex items-center py1 px1' onClick={handleActivateClick}>
                  <CrossCircledIcon className='icon--16x16 mr2' />
                  <span className='font-dark-grey h5'>Activate</span>
                </div>
              </div>
              <div className='MenuItem cursor--pointer'>
                <div className='flex items-center py1 px1' onClick={handleRemoveClick}>
                  <CrossCircledIcon className='icon--16x16 mr2' />
                  <span className='font-dark-grey h5'>Remove</span>
                </div>
              </div>
            </React.Fragment>
          ) : (
            <div className='MenuItem cursor--pointer'>
              <div className='flex items-center py1 px1' onClick={handleDeactivateClick}>
                <CrossCircledIcon className='icon--16x16 mr2' />
                <span className='font-dark-grey h5'>Deactivate</span>
              </div>
            </div>
          )}
        </ContextMenu>
      </div>
    )
  }

  React.useEffect(() => {
    if (!props.query) {
      fetchData({ pageIndex: 0, pageSize, active: props.type === 'active' })
    }
  }, [props.type, props.filters, props.isDeactive, props.isActive, props.query])

  React.useEffect(() => {
    if (props.products && !!props.products.length) {
      renderTableData()
    }
  }, [props.products])

  React.useEffect(() => {
    if (props.query) {
      fetchData({ pageIndex: 0, pageSize: 500, active: props.type === 'active' })
    }
  }, [props.query])

  const renderTableData = React.useCallback(() => {
    const filterData: IProviderProductObject[] = []
    if (props.products && props.products.length) {
      props.products
        .filter(
          (p: any) =>
            !props.query ||
            (props.query &&
              p.name.toLowerCase().includes(props.query.toLowerCase()) &&
              !p.deactivationDate === (props.type === 'active'))
        )
        .map((product: any) => {
          filterData.push(product)
        })
    }

    props.query ? setPageCount(1) : setPageCount(Math.ceil(props.productCount / pageSize))
    setData(filterData)
  }, [props.products, props.query])

  const fetchData = React.useCallback(async ({ pageSize, pageIndex, active }) => {
    setLoading(true)
    if (props.filters.organizationId && active !== undefined) {
      props.onComponentMount({
        ...props.filters,
        skip: pageIndex * pageSize,
        take: pageSize,
        active,
        sort: SORT_ORDER.ASC,
        orderBy: SORT_FIELDS.PRODUCT_NAME
      })
      if (!props.query) {
        setPageSize(pageSize)
      }
    }
    setLoading(false)
  }, [props.filters, props.isDeactive, props.isActive, props.query])

  return (
    <div className='relative px2 py2'>
      <div className='react-table'>
        <Table
          columns={columns}
          data={data || []}
          fetchData={fetchData}
          filters={props.filters}
          updateFilters={props.updateProductFilters}
          loading={loading}
          pageCount={pageCount}
          type={props.type}
          query={props.query}
        />
      </div>
    </div>
  )
}

export default withRouter(ProviderProductList)



// WEBPACK FOOTER //
// ./src/components/products/ProviderProductList.tsx